import { useState, useEffect } from 'react';
import { Clock, Check, X, Info, Calendar, User, Search, AlertCircle, Loader2 } from 'lucide-react';
import API_BASE_URL from '../../config';

const FreezeRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Pending');
  const [reviewingId, setReviewingId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actioning, setActioning] = useState(false);
  const [banner, setBanner] = useState({ type: null, message: '' });

  const userInfo = JSON.parse(localStorage.getItem('fitscan_user'));

  useEffect(() => {
    fetchRequests();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/pending?gymCode=${userInfo?.gymcode || ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (err) {
      console.error('Failed to load requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/history/gym?gymCode=${userInfo?.gymcode || ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setHistoryRequests(data);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const showBannerMessage = (type, message) => {
    setBanner({ type, message });
    setTimeout(() => setBanner({ type: null, message: '' }), 4000);
  };

  const handleApprove = async (id) => {
    setActioning(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          requestId: id,
          approve: true
        })
      });

      if (response.ok) {
        showBannerMessage('success', 'Freeze request approved successfully!');
        setRequests(prev => prev.filter(r => r.id !== id));
        fetchHistory();
      } else {
        const errText = await response.text();
        showBannerMessage('error', errText || 'Failed to approve request.');
      }
    } catch (err) {
      console.error('Approve error:', err);
      showBannerMessage('error', 'An error occurred.');
    } finally {
      setActioning(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      showBannerMessage('error', 'Rejection reason is required.');
      return;
    }
    setActioning(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          requestId: reviewingId,
          approve: false,
          rejectionReason: rejectionReason
        })
      });

      if (response.ok) {
        showBannerMessage('success', 'Freeze request rejected.');
        setRequests(prev => prev.filter(r => r.id !== reviewingId));
        fetchHistory();
        setShowRejectModal(false);
        setRejectionReason('');
        setReviewingId(null);
      } else {
        const errText = await response.text();
        showBannerMessage('error', errText || 'Failed to reject request.');
      }
    } catch (err) {
      console.error('Reject error:', err);
      showBannerMessage('error', 'An error occurred.');
    } finally {
      setActioning(false);
    }
  };

  const filteredRequests = requests.filter(r => {
    const nameMatch = r.memberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.memberUsername?.toLowerCase().includes(searchTerm.toLowerCase());
    return nameMatch;
  });

  const filteredHistory = historyRequests.filter(r => {
    const nameMatch = r.memberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.memberUsername?.toLowerCase().includes(searchTerm.toLowerCase());
    return nameMatch;
  });

  return (
    <div className="space-y-6">
      {/* Banner */}
      {banner.message && (
        <div className={`p-4 rounded-xl border flex items-center gap-2 ${banner.type === 'success' ? 'bg-[#6CD800]/10 border-[#6CD800]/25 text-[#6CD800]' : 'bg-red-500/10 border-red-500/25 text-red-500'}`}>
          <Info size={16} />
          <p className="text-xs font-semibold">{banner.message}</p>
        </div>
      )}

      {/* Header & Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Membership Freeze Requests</h1>
          <p className="text-xs text-gray-400 mt-1">Review and process temporary pause requests from members.</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          {/* Tabs */}
          <div className="flex bg-[#161616] p-1 rounded-xl border border-[#2b2b2b]">
            <button
              onClick={() => setFilterStatus('Pending')}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${filterStatus === 'Pending' ? 'bg-[#2a2a2a] text-[#6CD800]' : 'text-gray-400 hover:text-white'}`}
            >
              Pending ({requests.length})
            </button>
            <button
              onClick={() => setFilterStatus('History')}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${filterStatus === 'History' ? 'bg-[#2a2a2a] text-[#6CD800]' : 'text-gray-400 hover:text-white'}`}
            >
              History ({historyRequests.length})
            </button>
          </div>

          <div className="relative flex-1 sm:w-64">
            <input
              type="text"
              placeholder="Search member..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#161616] border border-[#2b2b2b] rounded-xl text-xs text-white outline-none focus:border-[#6CD800]/50 transition-colors"
            />
            <Search size={14} className="absolute left-3.5 top-3.5 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {filterStatus === 'History' ? (
        loadingHistory ? (
          <div className="flex flex-col items-center justify-center py-20 bg-[#161616] border border-[#2b2b2b] rounded-2xl">
            <Loader2 className="animate-spin text-[#6CD800] mb-3" size={32} />
            <p className="text-gray-400 text-xs font-medium">Loading history...</p>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-[#161616] border border-[#2b2b2b] rounded-2xl text-center px-4">
            <Clock size={40} className="text-gray-600 mb-4" />
            <h3 className="text-sm font-bold text-white">No History Records</h3>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">There are no approved, rejected, or cancelled freeze requests recorded.</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-[#161616] border border-[#2b2b2b] rounded-2xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#2b2b2b] text-[10px] text-gray-400 uppercase tracking-widest font-bold bg-[#1b1b1b]">
                  <th className="px-6 py-4">Member</th>
                  <th className="px-6 py-4">Requested Range</th>
                  <th className="px-6 py-4">Actual range</th>
                  <th className="px-6 py-4">Reason</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Processed Info</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#222] text-xs text-gray-300">
                {filteredHistory.map((req) => {
                  let statusBadgeClass = 'bg-gray-500/10 text-gray-400 border border-gray-500/20';
                  if (req.status === 1) {
                    statusBadgeClass = 'bg-[#6CD800]/10 text-[#6CD800] border border-[#6CD800]/20';
                  } else if (req.status === 2) {
                    statusBadgeClass = 'bg-red-500/10 text-red-400 border border-red-500/20';
                  } else if (req.status === 3) {
                    statusBadgeClass = 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
                  }

                  const statusLabels = {
                    0: 'Pending',
                    1: 'Approved',
                    2: 'Rejected',
                    3: 'Cancelled'
                  };

                  return (
                    <tr key={req.id} className="hover:bg-[#1a1a1a] transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-bold text-white">{req.memberName}</div>
                        <div className="text-[10px] text-gray-500">@{req.memberUsername}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-white">
                          {new Date(req.requestedStartDate).toLocaleDateString()}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {req.durationDays} days (to {new Date(req.computedEndDate).toLocaleDateString()})
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {req.actualEndDate ? (
                          <>
                            <div className="font-semibold text-white">
                              {new Date(req.requestedStartDate).toLocaleDateString()}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {req.actualDurationDays} days (to {new Date(req.actualEndDate).toLocaleDateString()})
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 max-w-xs truncate" title={req.reason}>
                        {req.reason || <span className="text-gray-600">No reason provided</span>}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${statusBadgeClass}`}>
                          {statusLabels[req.status] || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[10px] text-gray-400 space-y-0.5">
                        {req.reviewedBy && (
                          <div>By: <span className="text-white font-medium">{req.reviewedBy}</span></div>
                        )}
                        {req.reviewedAt && (
                          <div>On: {new Date(req.reviewedAt).toLocaleDateString()}</div>
                        )}
                        {req.rejectionReason && (
                          <div className="text-red-400 font-medium truncate max-w-[150px]" title={req.rejectionReason}>
                            Reason: {req.rejectionReason}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        /* Pending requests */
        loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-[#161616] border border-[#2b2b2b] rounded-2xl">
            <Loader2 className="animate-spin text-[#6CD800] mb-3" size={32} />
            <p className="text-gray-400 text-xs font-medium">Loading requests...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-[#161616] border border-[#2b2b2b] rounded-2xl text-center px-4">
            <Clock size={40} className="text-gray-600 mb-4" />
            <h3 className="text-sm font-bold text-white">No Pending Requests</h3>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">There are no pending freeze requests requiring your approval right now.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredRequests.map((req) => (
              <div key={req.id} className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-5 space-y-4 flex flex-col justify-between">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#222] border border-[#2b2b2b] flex items-center justify-center text-lime-400 text-sm font-bold">
                        <User size={18} />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-white">{req.memberName}</h4>
                        <p className="text-[10px] text-gray-500 mt-0.5">@{req.memberUsername}</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                      Pending
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 bg-[#0d0d0d] p-3 rounded-xl border border-[#222]">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Start Date</span>
                      <span className="text-xs font-semibold text-white flex items-center gap-1.5 mt-0.5">
                        <Calendar size={12} className="text-[#6CD800]" />
                        {new Date(req.requestedStartDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Duration</span>
                      <span className="text-xs font-semibold text-white flex items-center gap-1.5 mt-0.5">
                        <Clock size={12} className="text-[#6CD800]" />
                        {req.durationDays} Days
                      </span>
                    </div>
                  </div>

                  {req.reason && (
                    <div className="p-3 bg-[#1c1c1c] rounded-xl border border-[#252525] text-xs leading-relaxed text-gray-300">
                      <span className="font-semibold text-gray-400 block mb-1">Reason:</span>
                      "{req.reason}"
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-[#252525] flex gap-3">
                  <button
                    onClick={() => handleApprove(req.id)}
                    disabled={actioning}
                    className="flex-1 py-2.5 rounded-xl bg-[#6CD800] hover:bg-[#5bb300] disabled:opacity-50 text-zinc-950 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Check size={14} />
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      setReviewingId(req.id);
                      setShowRejectModal(true);
                    }}
                    disabled={actioning}
                    className="flex-1 py-2.5 rounded-xl border border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white disabled:opacity-50 font-bold text-xs flex items-center justify-center gap-1.5 transition-all"
                  >
                    <X size={14} />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowRejectModal(false)}>
          <div className="bg-[#181818] border border-[#2b2b2b] rounded-2xl max-w-sm w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <AlertCircle size={20} className="text-red-500" />
              Reject Freeze Request
            </h3>
            <div>
              <label className="block text-gray-400 text-[10px] uppercase tracking-widest font-bold mb-2">Rejection Reason</label>
              <textarea
                rows={3}
                placeholder="Enter stated cause..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-xs text-white outline-none focus:border-red-500/50 transition-colors resize-none"
              />
              <p className="text-[9px] text-gray-500 mt-1">Per gym terms of service, a stated cause must be provided for rejecting freeze requests.</p>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setReviewingId(null);
                }}
                className="px-4 py-2 rounded-lg bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-300 text-xs border border-[#3b3b3b] font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={actioning || !rejectionReason.trim()}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-medium"
              >
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreezeRequestsTab;
